import { defineStore } from "pinia";
import { nextTick } from "vue";

import type { AgentState } from "./Models/State";
import type { Transcription } from "@/Modules/Models/Domain/User";
import type { ConversationItem, AgentRatingQuery } from "@/Modules/Agent/Models/Domain";
import { AgentStage, AgentItemType } from "@/Modules/Agent/Models/Domain";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import useUserStore from "@/Modules/User/Store";
import useAppStore from "@/Modules/App/Store";
import useChatStore from "@/Modules/Chat/Store";
import useCustomerStore from "@/Modules/Customers/Store";

import deleteConversationAH from "./ActionHandlers/DeleteConversation";
import askQuestionAH from "./ActionHandlers/AskQuestion";
import fetchIntroAH from "./ActionHandlers/FetchIntro";
import fetchPatientsAH from "./ActionHandlers/FetchPatients";
import fetchConversationsAH from "./ActionHandlers/FetchConversations";
import fetchConversationAH from "./ActionHandlers/FetchConversation";
import fetchSuggestionsAH from "./ActionHandlers/FetchSuggestions";
import fetchUserConversationsAH from "./ActionHandlers/FetchUserConversations";
import sendRatingAH from "./ActionHandlers/SendRating";
import deleteRatingAH from "./ActionHandlers/DeleteRating";

import { getCurrentDateInSeconds } from "@/Utils/Date";
import { hasValue, keysOf } from "@/Utils/Objects";
import { isHubSpotLoaded } from "@/Modules/App/TrackersAndCookies/Hubspot";

const agentState: AgentState = {
  isLoadingData: false,
  patients: [],
  conversations: [],
  agentStage: null,
  conversation: [],
  suggestions: [],
  intro: "",
  activeConversation: null,
  isBotChatOpen: false,
  ratingAnswerId: null,
  isSavingRating: false,
};

export const useAgentStore = defineStore(StateNamespace.AGENT, {
  state: () => ({
    ...agentState,
    userStore: useUserStore(),
    appStore: useAppStore(),
    chatStore: useChatStore(),
    customerStore: useCustomerStore(),
  }),
  actions: {
    fetchConversations() {
      return fetchConversationsAH();
    },

    fetchConversation(conversationUuid: string) {
      return fetchConversationAH(conversationUuid);
    },

    fetchUserConversations(customerUuid: string) {
      return fetchUserConversationsAH(customerUuid);
    },

    fetchPatients() {
      return fetchPatientsAH();
    },

    fetchIntro(customerUuid: string) {
      return fetchIntroAH(customerUuid);
    },

    askQuestion(customerUuid: string, question: string) {
      return askQuestionAH(customerUuid, question);
    },

    fetchSuggestions(customerUuid: string) {
      return fetchSuggestionsAH(customerUuid);
    },

    setActiveConversation(conversation: ConversationItem) {
      this.activeConversation = conversation;

      this.fetchConversation(conversation.uuid);
    },

    setQuestion(question: string) {
      const questionDomain = {
        uuid: self.crypto.randomUUID(),
        type: AgentItemType.ASK,
        createdAt: getCurrentDateInSeconds(),
        updatedAt: 0,
        question: question,
        answer: "",
        ragSessionUuid: "",
        isRecent: false,
      };

      this.conversation.push(questionDomain);
    },

    markAsNotRecent(uuid: string) {
      const message = this.conversation.find((msg) => msg.uuid === uuid);

      if (hasValue(message) && message.isRecent) {
        message.isRecent = false;
      }
    },

    isActiveConversation(conversation: ConversationItem) {
      return (
        hasValue(this.activeConversation) &&
        this.activeConversation.customerUuid === conversation.customerUuid
      );
    },

    async deleteConversation(customerUuid: string) {
      const result = await deleteConversationAH(customerUuid);

      if (result) {
        return this.fetchConversations();
      }
    },

    closeConversation() {
      this.setActiveConversation(this.getAgent!);
    },

    toggleBotChat() {
      this.isBotChatOpen = !this.isBotChatOpen;

      if (isHubSpotLoaded()) {
        this.appStore.ubicateHubSpot();
      }
    },

    hideBot() {
      this.isBotChatOpen = false;

      if (isHubSpotLoaded()) {
        this.appStore.ubicateHubSpot();
      }
    },

    scrollToBottom() {
      nextTick(() => {
        const marker = document.getElementById("agent-end");

        if (marker && marker.scrollIntoView) {
          marker.scrollIntoView({ behavior: "smooth", block: "end" });
        }
      });
    },

    setRatingModalVisible(ratingId: string | null) {
      this.ratingAnswerId = ratingId;
    },

    sendRating(ratingUuid: string, ratingData: AgentRatingQuery) {
      if (!hasValue(ratingUuid)) return;

      return sendRatingAH(ratingUuid, ratingData);
    },

    deleteRating(ratingUuid: string) {
      if (!hasValue(ratingUuid)) return;

      return deleteRatingAH(ratingUuid);
    },

    clear() {
      keysOf(agentState).forEach((k) => {
        // @ts-expect-error clear store
        this[k] = agentState[k];
      });
    },
  },
  getters: {
    isLoading(state): boolean {
      return state.agentStage === AgentStage.LOADING;
    },

    isWriting(state): boolean {
      return state.agentStage === AgentStage.WRITING;
    },

    isBotEnabled(): boolean {
      const isAiEnabled =
        hasValue(this.chatStore.getActiveRoom) && !!this.chatStore.getActiveRoom!.isAiEnabled;

      return !this.appStore.isMobileViewport && isAiEnabled && this.userStore.getUserIsTherapist;
    },

    isBotAvailable(): boolean {
      return !this.appStore.isMobileViewport && this.userStore.getUserIsTherapist;
    },

    getAgent(state): ConversationItem | undefined {
      return state.conversations.find((conversation: ConversationItem) => conversation.isAgent === true);
    },

    getActiveConversation(state): ConversationItem {
      return state.activeConversation!;
    },

    getTranscription(): Transcription | null {
      return hasValue(this.customerStore.getCurrentCustomer)
        ? this.customerStore.getCurrentCustomer?.transcription
        : hasValue(this.chatStore.getActiveRoom)
          ? this.chatStore.getActiveRoom?.transcription
          : null;
    },
  },
});

export type AgentStore = ReturnType<typeof useAgentStore>;

export default useAgentStore;
